import { effect, Injectable, signal, WritableSignal, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { FormBuilder } from '@angular/forms';
import { Location } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Operations } from '@it2go/types';
import { ResourceEnum } from '../../../../../types/src/authorization/enum/resource.enum';
import { ActionEnum } from '../../../../../types/src/authorization/enum/action.enum';
import { GlobalGqlService } from '../../services/global.gql-service';
import { GqlApi } from '../../services/api.gql-service';
import { knownRouteParameters, knownRouteParametersKey } from '../content/content-routing.params';
import { NavigationService } from './navigation.service';
import { SessionService } from '../../services/session.service';
import { GLOBAL_STORE_TOKEN, GlobalStoreType } from '../../store/types/global-store.type';
import { filter } from 'rxjs';
import { map } from 'rxjs/operators';

export let router: Router;
export let globalStore: Store;
export let fb: FormBuilder;
export let locationNav: Location;
export let translate: TranslateService;
export let gqlApi: GqlApi;
export let navigationService: NavigationService;
export let sessionService: SessionService;

export const isAllowed: Record<Operations, WritableSignal<boolean>> = {} as Record<Operations, WritableSignal<boolean>>;
export const routeParams: Record<knownRouteParametersKey, WritableSignal<string>> = {} as Record<knownRouteParametersKey, WritableSignal<string>>;
export const permissionsFetched = signal(false);

const SERVICE_CONTEXT_KEY = 'serviceTypeContext';

export function goBack(): void {
    navigationService.back();
}

@Injectable({ providedIn: 'root' })
export class SharedGlobalService {
    private readonly _router = inject(Router);
    private readonly _store = inject(Store);
    private readonly _fb = inject(FormBuilder);
    private readonly _location = inject(Location);
    private readonly _translate = inject(TranslateService);
    private readonly _gqlApi = inject(GlobalGqlService);
    private readonly _globalStore = inject<GlobalStoreType>(GLOBAL_STORE_TOKEN);
    private readonly _navigationService = inject(NavigationService);
    private readonly _session = inject(SessionService);
    private readonly _route = inject(ActivatedRoute);


    constructor() {
        const _router = this._router;
        const _store = this._store;
        const _fb = this._fb;
        const _location = this._location;
        const _translate = this._translate;
        const _gqlApi = this._gqlApi;
        const _globalStore = this._globalStore;
        const _navigationService = this._navigationService;
        const _session = this._session;
        router = _router;
        globalStore = _store;
        fb = _fb;
        locationNav = _location;
        translate = _translate;
        gqlApi = _gqlApi as any;
        navigationService = _navigationService;
        sessionService = _session;

        Object.values(ResourceEnum).forEach((resource) => {
            Object.values(ActionEnum).forEach((action) => {
                isAllowed[`${resource}.${action}`] = signal(false);
            });
        });

        effect(() => {
            const rules = _globalStore.loggedUserStore.rules();
            Object.values(ResourceEnum).forEach((resource) => {
                Object.values(ActionEnum).forEach((action) => {
                    const key = `${resource}.${action}` as Operations;
                    isAllowed[key].set(key in rules);
                });
            });
        });

        this._router.events.pipe(
            filter(e => e instanceof NavigationEnd),
            map(() => {
                let route = this._router.routerState.snapshot.root;
                while (route.firstChild) {
                    route = route.firstChild;
                }
                return {
                    ...route.queryParams,
                    ...route.params,
                };
            })
        ).subscribe(params => {
            Object.keys(routeParams).forEach((key) => {
                (routeParams as any)[key].set(params?.[key] || '0');
            });
        });

        knownRouteParameters.forEach((it) => {
            routeParams[it] = signal('0');
        });

        _router.events.subscribe((it) => {
            if (it instanceof NavigationEnd) {
                let child = this._route.firstChild;
                while (child) {
                    const data = child.snapshot.data;
                    if (SERVICE_CONTEXT_KEY in data) {
                        this._globalStore.loggedUserStore.setServiceTypeContext(data[SERVICE_CONTEXT_KEY]);
                        break;
                    }
                    child = child.firstChild;
                }
            }
        });
    }

}
